// terminFormLogik.ts — pure Helfer des Termin-Fenster-Reworks (Termine-SP5,
// Review 2026-07-05: A7/A8/A9). Bewusst OHNE React/DOM, damit die Logik unter
// vitest (node-Env, nur *.test.ts) testbar bleibt:
// - zentrierte Fenster-Position (A7 — Fenster mittig statt Kaskade),
// - Teilnehmer-Kollaps „4 Chips + +N" (A8),
// - HTML-Erkennung des Termin-Bodys (A9, Entscheidung 64: HTML lesend rendern
//   + Plain-Edit).

/**
 * Fenster mittig im Viewport platzieren (A7). Ist der Viewport kleiner als das
 * Fenster, wird auf den Mindestabstand geklemmt (nie negativ — die Titelleiste
 * muss greifbar bleiben). Auf dem Phone rendert WindowLayer ohnehin Vollbild.
 */
export function zentrierteFensterPosition(
    viewport: { width: number; height: number },
    size: { width: number; height: number },
    abstand = 12,
): { x: number; y: number } {
    const x = Math.max(abstand, Math.round((viewport.width - size.width) / 2));
    const y = Math.max(abstand, Math.round((viewport.height - size.height) / 2));
    return { x, y };
}

/**
 * Teilnehmer-Kollaps (A8): kollabiert liefert die ersten `maxSichtbar` Chips +
 * die Anzahl der versteckten („+N weitere"); expandiert alle. Bei ≤ maxSichtbar
 * Einträgen gibt es nichts zu verstecken.
 */
export function kollabierteTeilnehmer<T>(
    alle: T[],
    expanded: boolean,
    maxSichtbar = 4,
): { sichtbar: T[]; versteckt: number } {
    if (expanded || alle.length <= maxSichtbar) {
        return { sichtbar: alle, versteckt: 0 };
    }
    return { sichtbar: alle.slice(0, maxSichtbar), versteckt: alle.length - maxSichtbar };
}

/**
 * Sieht der Termin-Body nach HTML aus? (A9, Entscheidung 64: HTML nur lesend
 * über SandboxedMailBody rendern; bearbeitet wird als Plain-Text.)
 * Graphs `body.contentType` ('html'|'text') ist maßgeblich; ohne contentType
 * entscheidet eine Tag-Heuristik (Teams-Einladungen sind immer HTML).
 * Leerer Body ⇒ false (dann direkt die Textarea zeigen).
 */
export function siehtAusWieHtml(content: string, contentType?: string | null): boolean {
    if (!content || !content.trim()) return false;
    const ct = contentType?.toLowerCase();
    if (ct === "html") return true;
    if (ct === "text") return false;
    return /<([a-z][a-z0-9]*)\b[^>]*>/i.test(content);
}

/** Vorbelegte Dauer eines Termins (PO 28.09.2026): „immer 30 min zwischen von und bis". */
export const STANDARD_DAUER_MIN = 30;

const zwei = (n: number): string => String(n).padStart(2, "0");

/** „Jetzt" auf das nächste volle 30-Minuten-Raster aufrunden (Default-Start der Neuanlage). */
export function aufHalbeStundeAufrunden(basis: Date): Date {
    const d = new Date(basis);
    d.setSeconds(0, 0);
    const m = d.getMinutes();
    if (m === 0 || m === 30) return d;
    if (m < 30) d.setMinutes(30);
    else {
        d.setMinutes(0);
        d.setHours(d.getHours() + 1);
    }
    return d;
}

/**
 * Das Ende, das nach einer Änderung von „von" vorbelegt wird (PO 28.09.2026):
 * „wenn ich von bearbeite, dann ist bis erstmal 30 min im Voraus". Liegt der
 * Start kurz vor Mitternacht, wandert das Ende-Datum auf den Folgetag — sonst
 * stünde ein Ende VOR dem Start im Formular.
 */
export function endeNachStart(
    startDate: Date,
    startTime: string,
    minuten = STANDARD_DAUER_MIN,
): { endDate: Date; endTime: string } {
    const t = /^(\d{1,2}):(\d{2})$/.exec(startTime.trim());
    const d = new Date(startDate);
    d.setHours(t ? Number(t[1]) : 0, t ? Number(t[2]) : 0, 0, 0);
    const ende = new Date(d.getTime() + minuten * 60 * 1000);
    const endDate = new Date(ende);
    endDate.setHours(0, 0, 0, 0);
    return { endDate, endTime: `${zwei(ende.getHours())}:${zwei(ende.getMinutes())}` };
}

/**
 * Die Felder, die im OUTLOOK-Termin stehen. Nur wenn sich davon etwas geändert
 * hat, darf „Speichern" den Termin in Graph patchen (PO 28.09.2026): Jeder
 * PATCH an einer Besprechung verschickt ein Update an alle Teilnehmer — und das
 * darf nicht passieren, nur weil jemand die Art (Kundentermin/Austausch/
 * Sonstige), die Farbe, die Kontakte oder den Vorgang geändert hat. Diese Dinge
 * leben ausschließlich im KMVS-Sidecar.
 */
export interface GraphFelder {
    subject: string;
    startDate: string | null;
    startTime: string;
    endDate: string | null;
    endTime: string;
    allDay: boolean;
    location: string;
    reminderPreset: string;
}

/**
 * Hat sich am Outlook-Termin etwas geändert? Neben dem Feldvergleich zählen
 * die Dirty-Flags der Felder, die nur dann gesendet werden, wenn der Nutzer
 * sie angefasst hat (Body, Teilnehmer/Raum, Serie, Privat).
 */
export function graphTerminGeaendert(
    vorher: GraphFelder | null,
    jetzt: GraphFelder,
    flags: { body: boolean; teilnehmer: boolean; serie: boolean; privat: boolean },
): boolean {
    if (flags.body || flags.teilnehmer || flags.serie || flags.privat) return true;
    if (!vorher) return true;
    return (Object.keys(jetzt) as (keyof GraphFelder)[]).some((k) => vorher[k] !== jetzt[k]);
}

/**
 * Ist das eine vollständige Uhrzeit „HH:mm" (00:00–23:59)? (T-267/T-269)
 *
 * Das native `<input type="time">` meldet in Edge/Chrome `""`, sobald die
 * Eingabe unvollständig oder gelöscht ist. Ging das ungeprüft an die API,
 * entstand `2026-10-08T:00` — und der Server lehnte mit 400 „end must be a
 * valid ISO 8601 date string" ab. Jetzt sperrt die Prüfung vorher „Speichern".
 */
export function uhrzeitGueltig(zeit: string | null | undefined): boolean {
    const m = /^(\d{2}):(\d{2})$/.exec((zeit ?? "").trim());
    if (!m) return false;
    return Number(m[1]) <= 23 && Number(m[2]) <= 59;
}

/** Teile einer Anschrift, wie `GET contact/:id` sie als `defaultAddress` liefert. */
export interface KontaktAnschrift {
    street?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: string | null;
}

/** Inlands-Kennungen: das Land bleibt im Ort dann weg (wie auf jedem Briefumschlag). */
const INLAND = new Set(["", "deutschland", "de", "deu", "germany", "brd"]);

/**
 * Die Standardanschrift eines Kontakts als einzeiliger Termin-Ort (T-267):
 * „Straße, PLZ Ort" — bei ausländischen Anschriften mit „, Land" dahinter.
 * Ohne verwertbare Anschrift ⇒ `""` (dann bleibt „Ort" leer).
 */
export function ortAusKontakt(
    kontakt: { defaultAddress?: KontaktAnschrift | null } | null | undefined,
): string {
    const a = kontakt?.defaultAddress;
    if (!a) return "";
    const land = (a.country ?? "").trim();
    return [
        (a.street ?? "").trim(),
        [(a.postalCode ?? "").trim(), (a.city ?? "").trim()].filter(Boolean).join(" "),
        INLAND.has(land.toLowerCase()) ? "" : land,
    ]
        .filter(Boolean)
        .join(", ");
}

/**
 * Darf das Termin-Fenster „Ort" automatisch mit der Anschrift des
 * Hauptkontakts belegen? (T-267)
 *
 * - Nie beim Bearbeiten: „Ort" ist ein Outlook-Feld, jede Änderung schickt ein
 *   Update an alle Teilnehmer (PO 28.09.2026).
 * - Nie, sobald der Nutzer „Ort" selbst angefasst hat.
 * - Sonst nur, wenn „Ort" leer ist oder noch den zuletzt automatisch gesetzten
 *   Wert trägt (Hauptkontakt gewechselt ⇒ dessen Anschrift).
 */
export function sollOrtAutomatischSetzen(p: {
    isEdit: boolean;
    ortVomNutzer: boolean;
    aktuellerOrt: string;
    letzterAutoOrt: string | null;
}): boolean {
    if (p.isEdit || p.ortVomNutzer) return false;
    const ort = p.aktuellerOrt.trim();
    return ort === "" || (p.letzterAutoOrt != null && ort === p.letzterAutoOrt.trim());
}

/**
 * Vorgang-Auswahl bei der Neuanlage (aus dem früheren Light-Dialog übernommen,
 * PO 28.09.2026): kein Vorgang, ein bestehender (vorbelegt aus `linkVorgangId`
 * oder gesucht) oder „neu erstellen" — der Erstell-Flow läuft erst NACH dem
 * Anlegen, weil er die eventId zum Binden braucht.
 */
export type VorgangWahl =
    | { kind: "none" }
    | { kind: "existing"; vorgangId: number; label: string }
    | { kind: "new" };

/**
 * Anzeige-Label eines Vorgangs im Termin-Fenster (T-268): der Betreff, die
 * Nummer nur als Rückfall (kein Titel, kein Leserecht, Abruf läuft noch).
 */
export function vorgangLabel(vorgangId: number, titel: string | null | undefined): string {
    const t = (titel ?? "").trim();
    return t || `Vorgang #${vorgangId}`;
}

/**
 * Den nachgeladenen Betreff in die Vorgang-Auswahl übernehmen (T-268) — aber
 * nur, solange noch GENAU dieser Vorgang gewählt ist. Hat der Nutzer die
 * Vorbelegung inzwischen entfernt oder umgewählt, darf die späte Antwort
 * nichts überschreiben (gleiche Referenz zurück ⇒ kein Re-Render).
 */
export function mitVorgangTitel(
    wahl: VorgangWahl,
    vorgangId: number,
    titel: string | null | undefined,
): VorgangWahl {
    if (wahl.kind !== "existing" || wahl.vorgangId !== vorgangId) return wahl;
    const label = vorgangLabel(vorgangId, titel);
    return label === wahl.label ? wahl : { ...wahl, label };
}

/**
 * Wessen Adressen das Teilnehmerfeld beim Klick vorschlägt — analog zum
 * Mail-Fenster (T-22/T-256): der Hauptkontakt des Termins zuerst, danach die
 * weiteren zugewiesenen Kontakte und — kommt der Termin aus einem Vorgang
 * oder ist einer gewählt — dessen Kunde und Kontakte. Doppelte und ungültige
 * IDs fallen raus. Bei einer Firma liefert das Backend auch Zentrale und
 * Ansprechpartner mit.
 */
export function teilnehmerVorschlagKontext(
    kontaktIds: readonly number[],
    vorgang: { kundeId?: number | null; contactIds?: readonly number[] | null } | null,
): { kontextContactId: number | null; kontextContactIds: number[] } {
    const alle = [
        ...kontaktIds,
        ...(vorgang?.kundeId != null ? [vorgang.kundeId] : []),
        ...(vorgang?.contactIds ?? []),
    ].filter((id, i, liste): id is number => Number.isInteger(id) && id > 0 && liste.indexOf(id) === i);
    const [erster = null, ...weitere] = alle;
    return { kontextContactId: erster, kontextContactIds: weitere };
}

/**
 * Kontakte, die das Termin-Fenster selbst aus einem gewählten Teilnehmer
 * zugewiesen hat: Kontakt-Id → die Teilnehmeradressen (klein geschrieben),
 * über die er kam. Nur diese Kontakte darf das Entfernen eines Teilnehmers
 * wieder mitnehmen — von Hand gewählte, vorbelegte oder geladene nie.
 */
export type AutoKontakte = Readonly<Record<number, readonly string[]>>;

interface KontaktRef {
    id: number;
}

/**
 * Teilnehmer aus einem Adressvorschlag gewählt: den zugehörigen KMVS-Kontakt
 * dem Termin zuweisen — hinten angehängt, damit ein schon gesetzter
 * Hauptkontakt Hauptkontakt bleibt. Ist der Kontakt schon zugewiesen, kommt
 * er nicht doppelt; war er von Hand gesetzt, bleibt er das auch (kein
 * Automatik-Merker). Interne Adressen (Kollegen, eigenes Postfach) und
 * Treffer ohne Kontakt setzen nichts.
 */
export function kontaktAusTeilnehmer<K extends KontaktRef>(
    kontakte: readonly K[],
    auto: AutoKontakte,
    treffer: { email: string; contactId?: number | null },
    neuerKontakt: (id: number) => K,
    interneAdressen: ReadonlySet<string> = new Set(),
): { kontakte: K[]; auto: AutoKontakte; hinzugefuegt: boolean } {
    const id = treffer.contactId;
    const adresse = treffer.email.trim().toLowerCase();
    const unveraendert = { kontakte: [...kontakte], auto, hinzugefuegt: false };
    if (id == null || !Number.isInteger(id) || id <= 0 || !adresse) return unveraendert;
    if (interneAdressen.has(adresse)) return unveraendert;
    if (kontakte.some((k) => k.id === id)) {
        const bisher = auto[id];
        // Von Hand gesetzt: bleibt von Hand gesetzt.
        if (!bisher || bisher.includes(adresse)) return unveraendert;
        return { kontakte: [...kontakte], auto: { ...auto, [id]: [...bisher, adresse] }, hinzugefuegt: false };
    }
    return {
        kontakte: [...kontakte, neuerKontakt(id)],
        auto: { ...auto, [id]: [adresse] },
        hinzugefuegt: true,
    };
}

/**
 * Teilnehmer entfernt: ein Kontakt, den NUR Teilnehmerauswahlen gesetzt haben,
 * geht mit, sobald keine dieser Adressen mehr eingetragen ist. Alles andere
 * bleibt stehen.
 */
export function kontaktNachTeilnehmerEntfernt<K extends KontaktRef>(
    kontakte: readonly K[],
    auto: AutoKontakte,
    adresse: string,
): { kontakte: K[]; auto: AutoKontakte } {
    const lc = adresse.trim().toLowerCase();
    const naechsteAuto: Record<number, readonly string[]> = {};
    const raus = new Set<number>();
    for (const [schluessel, adressen] of Object.entries(auto)) {
        const id = Number(schluessel);
        const rest = adressen.filter((a) => a !== lc);
        if (rest.length === 0) raus.add(id);
        else naechsteAuto[id] = rest;
    }
    return { kontakte: kontakte.filter((k) => !raus.has(k.id)), auto: naechsteAuto };
}

/**
 * Kontaktliste von Hand geändert: Merker für Kontakte, die der Nutzer selbst
 * entfernt hat, verwerfen — taucht derselbe Kontakt später wieder auf, gilt er
 * als von Hand gesetzt.
 */
export function autoKontakteBereinigen(auto: AutoKontakte, kontakte: readonly KontaktRef[]): AutoKontakte {
    const ids = new Set(kontakte.map((k) => k.id));
    const naechste: Record<number, readonly string[]> = {};
    let geaendert = false;
    for (const [schluessel, adressen] of Object.entries(auto)) {
        if (ids.has(Number(schluessel))) naechste[Number(schluessel)] = adressen;
        else geaendert = true;
    }
    return geaendert ? naechste : auto;
}
